import React from "react";
import moment from "moment";
import { Card, Alert, Row, Col } from "antd";
import { Chart, Geom, Axis, Coord, Tooltip } from 'bizcharts';
import { getColorByScore } from "./utils";

const { Meta } = Card;

function getFormattedDate(date: string) {
    return moment.utc(date).local().format("MMM DD, YYYY [at] hh:mm A");
}

type Props = {
    url: string;
    checkedAt: string;
    screenshotUrl: string | null;
    overallScore: number;
    executiveSummary: string;
    dimensionData: { key: string; score: number; dimensionName: string }[];
    extra?: React.ReactNode;
}

const scale = {
    score: {
        min: 0,
        max: 100,
    },
};

function useIsNarrow(maxWidth = 576) {
    const query = `(max-width: ${maxWidth}px)`;
    const [isNarrow, setIsNarrow] = React.useState(
        () => typeof window !== "undefined" && window.matchMedia(query).matches
    );

    React.useEffect(() => {
        const mql = window.matchMedia(query);
        const handler = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
        setIsNarrow(mql.matches);
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
    }, [query]);

    return isNarrow;
}

function wrapLabel(text: string, maxLen = 16) {
    const words = text.split(' ');
    const lines: string[] = [];
    let current = '';
    for (const word of words) {
        if (current && (current + ' ' + word).length > maxLen) {
            lines.push(current);
            current = word;
        } else {
            current = current ? current + ' ' + word : word;
        }
    }
    if (current) lines.push(current);
    return lines.join('\n');
}

type RadarChartProps = {
    dimensionData: Props["dimensionData"];
    overallScore: number;
};

const RadarChart = ({ dimensionData, overallScore }: RadarChartProps) => {
    const [center, setCenter] = React.useState<{ x: number; y: number } | null>(null);
    const isNarrow = useIsNarrow();

    const data = dimensionData.map((d) => ({ item: d.dimensionName, score: d.score }));
    const scoreColor = getColorByScore(overallScore);

    // The horizontal padding is the room the axis labels live in on either side
    // of the polygon. 70px comfortably fits the longest label line while leaving
    // the (now wider span-8) score column enough width for a clear polygon across
    // the whole desktop range. On phones the chart spans the full card width, so
    // the labels can wrap less aggressively.
    const chartHeight = 300;
    const horizontalPadding = 70;
    const labelMaxLen = isNarrow ? 12 : 16;
    const labelFontSize = isNarrow ? 11 : 12;

    const handleChart = (chart: any) => {
        const updateCenter = () => {
            const coord = typeof chart.getCoord === "function" ? chart.getCoord() : chart.get("coord");
            if (coord && coord.center) {
                const { x, y } = coord.center;
                setCenter((prev) => (prev && prev.x === x && prev.y === y ? prev : { x, y }));
            }
        };
        updateCenter();
        chart.on("afterpaint", updateCenter);
    };

    return (
        <div className="radar-chart">
            <Chart data={data} scale={scale} forceFit height={chartHeight} padding={[30, horizontalPadding, 30, horizontalPadding]} onGetG2Instance={handleChart}>
                <Tooltip />
                <Coord type="polar" radius={0.75} />
                <Axis
                    name="item"
                    line={null}
                    tickLine={null}
                    label={{
                        autoRotate: false,
                        offset: 20,
                        textStyle: { textAlign: 'center', fontSize: labelFontSize },
                        formatter: (text: string) => wrapLabel(text, labelMaxLen),
                    }}
                    grid={{
                        lineStyle: {
                            lineDash: [0, 0]
                        }
                    }}
                />
                <Axis
                    name="score"
                    min={0}
                    label={null}
                    line={null}
                    tickLine={null}
                    grid={{
                        type: 'polygon',
                        lineStyle: {
                            stroke: '#E8E8E8',
                            lineWidth: 1,
                        },
                    }}
                />
                <Geom type="line" position="item*score" size={2} color={scoreColor} />
                <Geom type="point" position="item*score" size={4} shape="circle" color={scoreColor} />
                <Geom type="area" position="item*score" opacity={0.2} color={scoreColor} />
            </Chart>
            {center && (
                <div
                    className="radar-chart-average"
                    style={{ left: center.x, top: center.y }}
                >
                    <span className="value">{overallScore}/100</span>
                    {/* <span className="label">Visual SEO Score</span> */}
                </div>
            )}
        </div>
    );
};

export default function VisualSeoHeader({ url, checkedAt, screenshotUrl, overallScore, executiveSummary, dimensionData, extra }: Props) {
    return (
        <Row type="flex" gutter={24} className="visual-seo-body-row">
            <Col span={18} className="visual-seo-header-col">
                <Card
                    className="visual-seo-header-card"
                    title={
                        <Meta title={url} description={`Last checked on ${getFormattedDate(checkedAt)}`} />
                    }
                    extra={extra}
                >
                    <Alert
                        className="visual-seo-info-alert"
                        type="info"
                        showIcon
                        message={
                            <span>
                                <strong>Visual SEO Analysis</strong> shows how well your page's design and layout convert the visitors that search engines and AI tools send you.
                                We review six dimensions that decide whether someone stays and acts - from the first screen they see to the path that leads them to take action - and pinpoint
                                exactly where your design helps or gets in the way, with fixes ranked by effort and impact. Clear, frictionless pages also keep visitors engaged longer,
                                and that dwell time and return behavior are signals search engines reward.
                            </span>
                        }
                    />

                    <div className="visual-seo-header-content">
                        <div className="header-item screenshot-item">
                            <div className="monitor">
                                {screenshotUrl
                                    ? <img alt="Page screenshot" src={screenshotUrl} />
                                    : <div className="no-screenshot" />}
                            </div>
                        </div>

                        <div className="header-item summary-container">
                            <div className="summary-item summary" dangerouslySetInnerHTML={{ __html: executiveSummary }} />
                        </div>
                    </div>
                </Card>
            </Col>

            <Col span={6} className="visual-seo-score-col">
                <Card
                    className="visual-seo-score-card"
                    title={
                        <Meta title={"Visual SEO Score"} description={"How your overall score breaks down across the six dimensions"} />
                    }
                >
                    <div className="seo-score">
                        <RadarChart dimensionData={dimensionData} overallScore={overallScore} />
                    </div>
                </Card>
            </Col>
        </Row>
    );
}
